import graphql__aa1f47372b50bd73b368 from "./__generated__/ContactMarketingView_contact.graphql.ts";
import graphql__0834e84273a4da7feef0 from "./__generated__/ContactMarketingViewMembershipsQuery.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import Panel from "./presentational/Panel";
import {useFragment, useRelayEnvironment, fetchQuery} from "react-relay/hooks";
import {hideModal, showModal} from "../store/actions";
import {connect, ConnectedProps} from "react-redux";
import {useOutletContext} from "react-router";
import {useIntl} from "react-intl";
import Table from "./presentational/table/Table";
import TableHeader from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import StatusBadge from "./presentational/StatusBadge";
import ActionButton from "./presentational/button/ActionButton";
import {faPlusCircle, faTrash} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SubscribeContactToListDialog from "./dialog/SubscribeContactToListDialog";
import RemoveContactFromListDialog from "./dialog/RemoveContactFromListDialog";

type Props = {}

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type ContactMarketingViewProps = Props & ConnectedProps<typeof connector>;

const ContactMarketingView: React.FC<ContactMarketingViewProps> = (props) => {
    const intl = useIntl();
    const queryRef: any = useOutletContext();
    const relayEnvironment = useRelayEnvironment();
    const {dispatchShowModal, dispatchHideModal} = props;

    const contact = useFragment(
        graphql__aa1f47372b50bd73b368,
        queryRef
    );

    const [memberships, setMemberships] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);

    const loadMemberships = React.useCallback(() => {
        setIsLoading(true);
        fetchQuery(relayEnvironment, graphql__0834e84273a4da7feef0, {contactIdentifier: contact.contactIdentifier}).toPromise().then((data: any) => {
            setMemberships(data?.contactMailingLists || []);
            setIsLoading(false);
        });
    }, [relayEnvironment, contact.contactIdentifier]);

    React.useEffect(() => {
        loadMemberships();
    }, [loadMemberships]);

    if (contact == null) {
        return <p>{intl.formatMessage({id: 'contact.notFound'})}</p>;
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusStates = () => ({
        green: [intl.formatMessage({id: 'mailingList.member.status.subscribed'})],
        gray: [intl.formatMessage({id: 'mailingList.member.status.unsubscribed'})],
    });

    const formatStatus = (status: string) => {
        switch (status) {
            case 'SUBSCRIBED': return intl.formatMessage({id: 'mailingList.member.status.subscribed'});
            case 'UNSUBSCRIBED': return intl.formatMessage({id: 'mailingList.member.status.unsubscribed'});
            default: return status;
        }
    };

    const showSubscribeDialog = () => {
        dispatchShowModal({
            body: (
                <SubscribeContactToListDialog
                    contactIdentifier={contact.contactIdentifier}
                    handleCloseDialog={() => {
                        dispatchHideModal();
                        loadMemberships();
                    }}
                />
            )
        });
    };

    const showRemoveDialog = (membership: any) => {
        dispatchShowModal({
            body: (
                <RemoveContactFromListDialog
                    contactIdentifier={contact.contactIdentifier}
                    mailingListIdentifier={membership.mailingListIdentifier}
                    mailingListName={membership.mailingList?.name || membership.mailingListIdentifier}
                    handleCloseDialog={() => {
                        dispatchHideModal();
                        loadMemberships();
                    }}
                />
            )
        });
    };

    return (
        <>
            <Panel
                headline={intl.formatMessage({id: 'contact.marketing.lists'})}
                buttons={
                    <ActionButton
                        onClick={showSubscribeDialog}
                        icon={faPlusCircle}
                        label={intl.formatMessage({id: 'button.addToList'})}
                    />
                }
            >
                {isLoading ? (
                    <div className="px-6 py-6 text-gray-500 text-center">
                        {intl.formatMessage({id: 'loading'})}
                    </div>
                ) : memberships.length === 0 ? (
                    <div className="px-6 py-6 text-gray-500 text-center">
                        {intl.formatMessage({id: 'contact.marketing.noLists'})}
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 relative overflow-y-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader>{intl.formatMessage({id: 'contact.marketing.header.listName'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'contact.marketing.header.subscribedAt'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'contact.marketing.header.unsubscribedAt'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'contact.marketing.header.status'})}</TableHeader>
                                    <TableHeader></TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {memberships.map((membership, index) => (
                                    <tr key={membership.mailingListIdentifier} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                        <TableData>
                                            <span className="font-medium">{membership.mailingList?.name || membership.mailingListIdentifier}</span>

                                        </TableData>
                                        <TableData>
                                            {formatDate(membership.subscribedAt)}
                                        </TableData>
                                        <TableData>
                                            {formatDate(membership.unsubscribedAt)}
                                        </TableData>
                                        <TableData>
                                            <StatusBadge
                                                value={formatStatus(membership.status)}
                                                states={getStatusStates()}
                                            />
                                        </TableData>
                                        <TableData className="text-right">
                                            <button
                                                onClick={() => showRemoveDialog(membership)}
                                                className="invisible group-hover:visible text-gray-400 hover:text-red-600 p-2"
                                                title={intl.formatMessage({id: 'button.removeFromList'})}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>
        </>
    );
}

export default connector(ContactMarketingView);
